/**
 * Tracks which consultation (if any) is currently open for editing.
 *
 * Pure local state — no API calls. Prevents multiple consultations from being
 * open simultaneously and lets distant components (e.g. the "Prescribe" button)
 * react to an active consultation without prop drilling.
 *
 * Callers are responsible for making their own API calls (create, reopen, etc.)
 * and then calling `open` to activate the consultation in the workspace.
 */
import { useQueryClient } from '@tanstack/react-query'
import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

interface ActiveConsultationContextValue {
  /** ID of the currently open consultation, or null. */
  consultationId: string | null
  /** Open a consultation for editing. No-ops if one is already active. */
  open: (id: string) => void
  /** Close the active consultation. */
  close: () => void
}

const ActiveConsultationContext = createContext<ActiveConsultationContextValue | null>(null)

interface ActiveConsultationProviderProps {
  children: ReactNode
  patientId: string
}

export function ActiveConsultationProvider({
  children,
  patientId,
}: ActiveConsultationProviderProps) {
  const [consultationId, setConsultationId] = useState<string | null>(null)
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  /** Clear any URL hash (e.g. #highlight anchors) when opening a consultation. */
  const clearHash = useCallback(() => {
    if (window.location.hash) navigate(pathname, { replace: true })
  }, [navigate, pathname])

  const open = useCallback(
    (id: string) => {
      if (consultationId !== null) {
        toast.warning('Please close the current consultation first')
        return
      }
      clearHash()
      setConsultationId(id)
    },
    [consultationId, clearHash]
  )

  const close = useCallback(() => {
    setConsultationId(null)
    queryClient.invalidateQueries({ queryKey: ['patient-timeline', patientId] })
  }, [queryClient, patientId])

  const value = useMemo(() => ({ consultationId, open, close }), [consultationId, open, close])

  return (
    <ActiveConsultationContext.Provider value={value}>
      {children}
    </ActiveConsultationContext.Provider>
  )
}

/** Access the active consultation context. Throws if used outside the provider. */
export function useActiveConsultation(): ActiveConsultationContextValue {
  const ctx = useContext(ActiveConsultationContext)
  if (!ctx) throw new Error('useActiveConsultation must be used within ActiveConsultationProvider')
  return ctx
}
